import { useEffect, useState } from 'react'
import { WarehouseDirectory, WarehousePolicySource } from '@/console/api/types'
import { refreshWarehousePolicies, useWarehouseDirectory, useWarehousePolicies } from '@/console/data/warehousePolicy'
import { PanelState } from '@/console/state/panelState'
import { useReportBusy } from '@/console/state/useReportBusy'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { PolicyEditor } from './PolicyEditor'
import { WarehouseHeader } from './WarehouseHeader'
import { WarehouseList } from './WarehouseList'
import styles from './Warehouse.module.css'

type StalePolicies = { code: string; source: WarehousePolicySource }

/**
 * LOGISPRD-15602 — 창고 탭 좌우 분할. 왼쪽 `창고 목록`(`s-wh-list`)에서 고른 창고를 오른쪽 `정책 편집`(`s-wh-policy`)에서
 * 활성 전환하고 정책을 편집한다.
 *
 * 직전 응답 유지(stale) — 재조회 중(`loading`)·재조회 실패(`error`)에도 같은 창고의 직전 정책 응답이 있으면 카드를 그대로 두어
 * 저장 직후 카드가 사라졌다 나타나는 깜빡임과 입력 손실을 막고, 실패면 오류를 카드 위에 함께 보인다. 직전 응답이 없으면(첫 조회 실패) 오류만 보인다.
 * 창고를 바꾸면 직전 응답을 버린다 — 남겨 두면 다른 창고를 거쳐 되돌아왔을 때 몇 분 전 값이 편집 가능한 카드로 다시 그려진다.
 * 목록도 같은 이유로 직전 응답을 유지한다(쓰기 후 목록 재조회 동안 헤더 · 행이 사라지지 않게).
 *
 * 저장 · 전환 진행 수를 세어 0 보다 크면 목록 · 검색 · 새로고침과 셸 탭바를 잠근다(`onBusyChange`).
 */
export const WarehouseWorkspace = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const directory = useWarehouseDirectory()
  const [code, setCode] = useState<string | null>(null)
  const policies = useWarehousePolicies(code)
  const [writing, setWriting] = useState(0)
  const [staleDirectory, setStaleDirectory] = useState<WarehouseDirectory | null>(null)
  const [stalePolicies, setStalePolicies] = useState<StalePolicies | null>(null)

  const busy = writing > 0
  useReportBusy(busy, onBusyChange)

  const directoryReady = directory.state.status === 'ready' ? directory.state.data : null
  useEffect(() => {
    if (directoryReady) {
      setStaleDirectory(directoryReady)
    }
  }, [directoryReady])

  const policiesReady = policies.state.status === 'ready' ? policies.state.data : null
  useEffect(() => {
    if (policiesReady && code !== null) {
      setStalePolicies({ code, source: policiesReady })
    }
    // code 는 일부러 dep 에서 뺀다. policiesReady 는 useWarehousePolicies(code) 파생이라 code 가 바뀌면 null 로 리셋됐다가
    // 새 창고 응답으로 다시 바뀐다 — 그 시점 code 는 항상 최신이다.
  }, [policiesReady])

  const directoryData = directoryReady ?? (directory.state.status === 'empty' ? null : staleDirectory)
  // 창고를 고른 직후 첫 프레임은 아직 조회가 시작되기 전이라 idle 이다 — 코드가 선택돼 있으면 로딩으로 본다.
  const policyState: PanelState<WarehousePolicySource> = code !== null && policies.state.status === 'idle' ? { status: 'loading' } : policies.state
  const source: WarehousePolicySource | null = policiesReady ?? (stalePolicies && stalePolicies.code === code ? stalePolicies.source : null)

  const select = (next: string): void => {
    if (busy || next === code) {
      return
    }
    setStalePolicies(null)
    setCode(next)
  }

  const refresh = (): void => {
    directory.refetch()
    policies.refetch()
  }

  const callbacks = {
    onSavingChange: (saving: boolean): void => setWriting((count) => Math.max(0, count + (saving ? 1 : -1))),
    onSaved: (): void => directory.refetch(),
  }
  const statusCallbacks = {
    ...callbacks,
    // 활성 여부가 바뀌면 정책 원천도 바뀔 수 있다(활성 목록 폴백) — 정책도 다시 읽는다.
    onSaved: (): void => {
      directory.refetch()
      if (code !== null) {
        // 훅의 refetch 는 진행 중인 첫 조회에 합쳐질 수 있다 — 전환 전 원천이 남지 않게 취소 후 새로 읽는다.
        refreshWarehousePolicies(code).catch(() => undefined)
      }
    },
  }

  const item = code === null ? undefined : directoryData?.items.find((w) => w.warehouseCode === code)

  return (
    <div className={styles.split}>
      <aside className={styles.listCol}>
        <WarehouseList state={directory.state} directory={directoryData} selected={code} locked={busy} onSelect={select} onRefresh={refresh} />
      </aside>

      <div className={styles.detailCol}>
        <SectionHeading
          id="s-wh-policy"
          level={2}
          title="정책 편집"
          badge={<SourceBadge tone="have">get-warehouse · upsert-warehouse-policy</SourceBadge>}
        />

        {code === null ? (
          <div className={styles.detailEmpty}>
            <span className={styles.detailEmptyTitle}>왼쪽에서 창고를 고르세요</span>
            <span className={styles.detailEmptySub}>활성 전환과 정책 6종을 여기서 편집해요.</span>
          </div>
        ) : (
          <div className={styles.detail}>
            <WarehouseHeader key={code} code={code} item={item} mode={directoryData?.mode ?? null} locked={busy} {...statusCallbacks} />

            {source !== null && policyState.status === 'error' ? <ResultState state={policyState}>{null}</ResultState> : null}
            {source === null ? <ResultState state={policyState}>{null}</ResultState> : <PolicyEditor source={source} {...callbacks} />}
          </div>
        )}
      </div>
    </div>
  )
}
